// DocumentRulesTab.tsx — Admin Panel -> Insurance Claims -> Document rules.
// The per-profile document checklist a claim is seeded from (docs/
// HMS_V4_INSURANCE_CLAIMS_DESIGN.md §4.9, Insurance Claims Phase 5.4,
// wireframe S9's 4th tab) — code/service/claim-head/rate/unit CRUD's sibling
// for `payer_document_rules`. Follows ServiceMasterTab.tsx's/PayersTab.tsx's
// exact pattern, including the DataTable convention the 2026-10-14 pass
// established (no card-per-row list for a new admin tab).
import { useState, useEffect, useCallback } from 'react';
import {
  getPayerDocumentRules, getPayerDocumentRuleProfiles, createPayerDocumentRule,
  updatePayerDocumentRule, deletePayerDocumentRule,
} from '../api';
import type { PayerDocumentRule } from '../types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { LoadingState } from '@/components/ui/States';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';

const STAGES = ['preauth', 'final', 'claim'] as const;

type Draft = {
  profile: string; stage: string; doc_key: string; label: string;
  required: boolean; sort_order: string; auto_source: string; notes: string;
};

const BLANK_DRAFT: Draft = { profile: 'irdai_cashless_claim', stage: 'claim', doc_key: '', label: '', required: false, sort_order: '0', auto_source: '', notes: '' };

function toDraft(r: PayerDocumentRule): Draft {
  return {
    profile: r.profile, stage: r.stage, doc_key: r.doc_key, label: r.label,
    required: r.required, sort_order: String(r.sort_order), auto_source: r.auto_source ?? '', notes: r.notes ?? '',
  };
}

export default function DocumentRulesTab() {
  const [profiles, setProfiles] = useState<Array<{ profile: string; stage: string }> | null>(null);
  const [profileFilter, setProfileFilter] = useState<string>('irdai_cashless_claim/claim');
  const [items, setItems] = useState<PayerDocumentRule[] | null>(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Draft>(BLANK_DRAFT);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Draft | null>(null);

  useEffect(() => {
    getPayerDocumentRuleProfiles().then(setProfiles).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const [profile = '', stage = ''] = profileFilter.split('/');
  const load = useCallback(() => {
    getPayerDocumentRules({ profile, stage }).then(setItems).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [profile, stage]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!draft.doc_key.trim() || !draft.label.trim()) return;
    setSaving(true); setError('');
    try {
      await createPayerDocumentRule({
        profile: draft.profile, stage: draft.stage as PayerDocumentRule['stage'], doc_key: draft.doc_key.trim(),
        label: draft.label.trim(), required: draft.required, sort_order: Number(draft.sort_order) || 0,
        auto_source: draft.auto_source.trim() || undefined, notes: draft.notes.trim() || undefined,
      });
      setDraft({ ...BLANK_DRAFT, profile: draft.profile, stage: draft.stage });
      setAdding(false);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setSaving(false);
  };

  const startEdit = (r: PayerDocumentRule) => { setEditingId(r.id); setEditDraft(toDraft(r)); };

  const saveEdit = async () => {
    if (!editingId || !editDraft) return;
    setError('');
    try {
      await updatePayerDocumentRule(editingId, {
        label: editDraft.label.trim(), required: editDraft.required, sort_order: Number(editDraft.sort_order) || 0,
        auto_source: editDraft.auto_source.trim() || null, notes: editDraft.notes.trim() || null,
      });
      setEditingId(null); setEditDraft(null);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const toggleActive = async (r: PayerDocumentRule) => {
    setError('');
    try { await updatePayerDocumentRule(r.id, { active: !r.active }); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const remove = async (r: PayerDocumentRule) => {
    setError('');
    try { await deletePayerDocumentRule(r.id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  if (!items || !profiles) return <LoadingState label="Loading document rules…" />;

  const columns: DataTableColumn<PayerDocumentRule>[] = [
    { key: 'sort', header: '#', align: 'right', render: (r) => r.sort_order },
    { key: 'doc_key', header: 'Key', render: (r) => <span className="font-medium">{r.doc_key}</span> },
    {
      key: 'label', header: 'Label', sortable: true, sortValue: (r) => r.label,
      render: (r) => <>{r.label}{!r.active && <Badge variant="neutral" className="ml-2">Inactive</Badge>}</>,
    },
    { key: 'required', header: 'Required', align: 'center', render: (r) => r.required ? '✔' : '' },
    { key: 'auto_source', header: 'Auto-source', render: (r) => r.auto_source || <span className="text-ink-muted">upload only</span> },
    {
      key: 'actions', header: '', align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => startEdit(r)}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={() => toggleActive(r)}>{r.active ? 'Deactivate' : 'Reactivate'}</Button>
          <Button size="sm" variant="danger" onClick={() => remove(r)}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="fade-in">
      <div className="mb-3 text-xs text-ink-muted">
        The per-profile document checklist a claim is seeded from (once per claim, at creation) — what a payer
        actually needs for a cashless/reimbursement/scheme submission, which items auto-resolve from something
        this hospital already generated (the Main/Breakup bill, a pre-auth form, …), and which are required
        before the claim can move to &quot;submitted&quot;.
      </div>

      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <FormField label="Profile / stage">
          <NativeSelect value={profileFilter} onChange={(e) => setProfileFilter(e.target.value)} className="w-64">
            {profiles.map((p) => <option key={`${p.profile}/${p.stage}`} value={`${p.profile}/${p.stage}`}>{p.profile} / {p.stage}</option>)}
          </NativeSelect>
        </FormField>
        {!adding && <Button size="sm" onClick={() => { setDraft({ ...BLANK_DRAFT, profile, stage }); setAdding(true); }}>+ Add item</Button>}
      </div>

      {adding && (
        <Card className="mb-4 p-4">
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
            <FormField label="doc_key" required>
              <Input value={draft.doc_key} onChange={(e) => setDraft((d) => ({ ...d, doc_key: e.target.value.replace(/\s+/g, '_').toLowerCase() }))} placeholder="e.g. vaccination_certificate" />
            </FormField>
            <FormField label="Label" required className="col-span-2">
              <Input value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))} />
            </FormField>
            <FormField label="Stage">
              <NativeSelect value={draft.stage} onChange={(e) => setDraft((d) => ({ ...d, stage: e.target.value }))}>
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Sort order">
              <Input type="number" value={draft.sort_order} onChange={(e) => setDraft((d) => ({ ...d, sort_order: e.target.value }))} />
            </FormField>
            <FormField label="Auto-source key (optional)">
              <Input value={draft.auto_source} onChange={(e) => setDraft((d) => ({ ...d, auto_source: e.target.value }))} placeholder="e.g. main_bill" />
            </FormField>
            <FormField label="Notes" className="col-span-3">
              <Input value={draft.notes} onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
            </FormField>
          </div>
          <label className="mb-3 flex items-center gap-1.5 text-xs">
            <Checkbox checked={draft.required} onCheckedChange={(v) => setDraft((d) => ({ ...d, required: !!v }))} />
            Required to submit the claim
          </label>
          <div className="flex gap-2">
            <Button size="sm" disabled={saving || !draft.doc_key.trim() || !draft.label.trim()} loading={saving} onClick={submit}>Save item</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setDraft(BLANK_DRAFT); }}>Cancel</Button>
          </div>
        </Card>
      )}

      {editingId != null && editDraft && (
        <Card className="mb-4 p-4">
          <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
            <FormField label="doc_key"><Input value={editDraft.doc_key} disabled /></FormField>
            <FormField label="Label" className="col-span-2">
              <Input value={editDraft.label} onChange={(e) => setEditDraft((d) => d && ({ ...d, label: e.target.value }))} />
            </FormField>
            <FormField label="Sort order">
              <Input type="number" value={editDraft.sort_order} onChange={(e) => setEditDraft((d) => d && ({ ...d, sort_order: e.target.value }))} />
            </FormField>
            <FormField label="Auto-source key">
              <Input value={editDraft.auto_source} onChange={(e) => setEditDraft((d) => d && ({ ...d, auto_source: e.target.value }))} />
            </FormField>
            <FormField label="Notes" className="col-span-3">
              <Input value={editDraft.notes} onChange={(e) => setEditDraft((d) => d && ({ ...d, notes: e.target.value }))} />
            </FormField>
          </div>
          <label className="mb-3 flex items-center gap-1.5 text-xs">
            <Checkbox checked={editDraft.required} onCheckedChange={(v) => setEditDraft((d) => d && ({ ...d, required: !!v }))} />
            Required to submit the claim
          </label>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveEdit}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditDraft(null); }}>Cancel</Button>
          </div>
        </Card>
      )}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(r) => r.id}
        rowClassName={(r) => (r.active ? undefined : 'opacity-50')}
        emptyLabel="No items for this profile/stage"
        emptyHint="Add one above."
      />
    </div>
  );
}
